export interface MarkdownSection {
  title: string
  slug: string
  description: string
}

// A level two ATX heading, with the leading spaces and closing hashes
// CommonMark allows. Rejects `###`.
const H2_HEADING = /^ {0,3}##(?!#)\s+(.*?)(?:\s+#+)?\s*$/
const CODE_FENCE = /^ {0,3}(?:`{3,}|~{3,})/
const COMBINING_MARKS = /[\u0300-\u036f]/g
const LEADING_ORDINAL = /^\s*\d+[.)]\s+/
// An explicit heading anchor, `## Cruises {#cruises}`. Preferred over the
// slugified title because it survives translation.
const EXPLICIT_ANCHOR = /^(.*?)\s*\{#([\w.:-]+)\}$/

/**
 * Rebuilds a section anchor from its heading. Contentful stored a hand written
 * slug per section; the markdown only carries the heading text.
 */
export function slugifyHeading(heading: string): string {
  return heading
    // Anchors were built from the words alone, so "1. COLLECTION OF PERSONAL
    // INFORMATION" keeps "collection-of-personal-information".
    .replace(LEADING_ORDINAL, '')
    // So that "Société" resolves to "societe" rather than "socit".
    .normalize('NFD')
    .replace(COMBINING_MARKS, '')
    .toLowerCase()
    // Ahead of the strip below, so "Terms & Conditions" keeps its "-and-".
    .replace(/&/g, ' and ')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
}

function nextAvailableSlug(slug: string, taken: Set<string>): string {
  if (!taken.has(slug)) {
    return slug
  }

  let suffix = 2
  while (taken.has(`${slug}-${suffix}`)) {
    suffix += 1
  }

  return `${slug}-${suffix}`
}

/**
 * Splits a legal page's markdown body back into the sections it was assembled
 * from, one per level two heading, keeping their headings and anchors.
 *
 * Content ahead of the first heading becomes an untitled section, which covers
 * the pages that are a single block of markdown.
 *
 * A heading may carry an explicit `{#anchor}`; otherwise the anchor is
 * slugified from the heading text and so differs between translations.
 */
export function parseMarkdownSections(markdown: string): Array<MarkdownSection> {
  const sections: Array<MarkdownSection> = []
  const takenSlugs = new Set<string>()

  let title = ''
  let explicitAnchor = ''
  let lines: Array<string> = []
  let insideCodeFence = false

  const closeSection = () => {
    const description = lines.join('\n').trim()

    if (!title && !description) {
      return
    }

    // An untitled section renders no heading, so its slug is only a React key.
    const anchor = explicitAnchor || slugifyHeading(title) || `section-${sections.length + 1}`
    const slug = nextAvailableSlug(anchor, takenSlugs)

    takenSlugs.add(slug)
    sections.push({ title, slug, description })
  }

  for (const line of markdown.split('\n')) {
    if (CODE_FENCE.test(line)) {
      insideCodeFence = !insideCodeFence
    }

    const heading = insideCodeFence ? null : H2_HEADING.exec(line)

    if (heading) {
      closeSection()
      const anchored = EXPLICIT_ANCHOR.exec(heading[1].trim())
      title = (anchored ? anchored[1] : heading[1]).trim()
      explicitAnchor = anchored ? anchored[2] : ''
      lines = []
      continue
    }

    lines.push(line)
  }

  closeSection()

  return sections
}
